import React, { useState } from 'react';
import { Building2, TrendingUp, Users, Star, CheckCircle } from 'lucide-react';
import PageLayout, { PageHero, PageSection, PageCTA } from '../components/common/PageLayout';
import SEOHead from '../components/common/SEOHead';
import { useLanguage } from '../i18n/LanguageContext';

const GALLERY = [
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/lu5h35al_8E4176D1-0298-41D1-A1FE-FEC0AA134D5B.png', title: 'Torres Residenciales' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/397vlsv3_129A0250-D9D5-45EB-8A63-2051B1B34E52.png', title: 'Desarrollo Premium' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/6lghk188_6D5BE1DD-A003-4661-8A21-A95E8DAF5F00.png', title: 'Inauguración' },
  { url: 'https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/2xztpazu_0CDB6C67-7F8E-4A0A-A127-873DB57E2172.png', title: 'Lanzamiento Nocturno' },
];

const benefitIcons = [Building2, TrendingUp, Users, Star];

const LanzamientosInmobiliariosPage = () => {
  const [lightboxImage, setLightboxImage] = useState(null);
  const { language } = useLanguage();

  const content = {
    es: {
      title: "Lanzamientos",
      titleHighlight: "Inmobiliarios",
      subtitle: "Inaugura tus desarrollos con un espectáculo que eleve tu proyecto al cielo y acelere tus ventas.",
      description: "Creamos experiencias únicas que posicionan tu desarrollo inmobiliario como exclusivo y premium.",
      whyChoose: "¿Por qué elegir",
      whyChooseHighlight: "NexAir Elements",
      benefits: [
        { title: "Posicionamiento Premium", desc: "Tu desarrollo se posiciona como exclusivo y de alto nivel en el mercado." },
        { title: "Acelera Ventas", desc: "Genera expectativa y urgencia que se traduce en preventas y cierre de unidades." },
        { title: "Cobertura Mediática", desc: "Atrae prensa y medios que multiplican el alcance de tu lanzamiento." },
        { title: "Experiencia VIP", desc: "Tus inversionistas y compradores viven un momento inolvidable." },
      ],
      services: "Servicios para",
      servicesHighlight: "Inmobiliario",
      servicesList: [
        "Show de drones con render del edificio en el cielo",
        "Proyección del logo del desarrollo",
        "Pantalla de agua para presentaciones",
        "Efectos especiales para el momento del reveal",
        "Iluminación arquitectónica del proyecto",
      ],
      gallery: "Proyectos",
      galleryHighlight: "Realizados",
      ctaTitle: "¿Listo para lanzar",
      ctaTitleHighlight: "tu desarrollo",
      ctaDescription: "Contáctanos y diseñemos juntos un lanzamiento inolvidable que acelere tus ventas.",
    },
    en: {
      title: "Real Estate",
      titleHighlight: "Launches",
      subtitle: "Inaugurate your developments with a spectacle that elevates your project to the sky and accelerates your sales.",
      description: "We create unique experiences that position your real estate development as exclusive and premium.",
      whyChoose: "Why choose",
      whyChooseHighlight: "NexAir Elements",
      benefits: [
        { title: "Premium Positioning", desc: "Your development is positioned as exclusive and high-end in the market." },
        { title: "Accelerate Sales", desc: "Generate expectation and urgency that translates into pre-sales and unit closings." },
        { title: "Media Coverage", desc: "Attract press and media that multiply the reach of your launch." },
        { title: "VIP Experience", desc: "Your investors and buyers live an unforgettable moment." },
      ],
      services: "Services for",
      servicesHighlight: "Real Estate",
      servicesList: [
        "Drone show with building render in the sky",
        "Development logo projection",
        "Water screen for presentations",
        "Special effects for the reveal moment",
        "Architectural lighting of the project",
      ],
      gallery: "Completed",
      galleryHighlight: "Projects",
      ctaTitle: "Ready to launch",
      ctaTitleHighlight: "your development",
      ctaDescription: "Contact us and let's design together an unforgettable launch that accelerates your sales.",
    }
  };

  const c = content[language] || content.es;

  return (
    <PageLayout>
      <SEOHead 
        title={language === 'es' ? "Lanzamientos Inmobiliarios con Drones | Shows para Desarrollos | NexAir" : "Real Estate Launches with Drones | Development Shows | NexAir"}
        description={language === 'es' ? "Shows de drones y experiencias inmersivas para lanzamientos inmobiliarios. Posiciona tu desarrollo como premium y acelera tus ventas con espectáculos únicos." : "Drone shows and immersive experiences for real estate launches. Position your development as premium and accelerate sales with unique spectacles."}
        keywords={language === 'es' ? "lanzamiento inmobiliario, drones para inmobiliarias, show drones desarrollo, inauguracion edificios" : "real estate launch, drones for developers, drone show development, building inauguration"}
        canonicalUrl="https://nexair.com.mx/lanzamientos-inmobiliarios"
      />
      
      <PageHero 
        image="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/lu5h35al_8E4176D1-0298-41D1-A1FE-FEC0AA134D5B.png"
        title={c.title}
        titleHighlight={c.titleHighlight}
        subtitle={c.subtitle}
        description={c.description}
      />
      
      {/* Benefits */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.whyChoose} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{c.whyChooseHighlight}</span>?
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {c.benefits.map((benefit, index) => {
            const Icon = benefitIcons[index];
            return (
              <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-cyan-500/50 transition-all">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-7 h-7 text-cyan-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2 font-heading">{benefit.title}</h3>
                <p className="text-gray-400">{benefit.desc}</p>
              </div>
            );
          })}
        </div>
      </PageSection>

      {/* Services */}
      <PageSection className="bg-[#050a15]">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 font-heading">
              {c.services} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{c.servicesHighlight}</span>
            </h2>
            <ul className="space-y-4">
              {c.servicesList.map((item, index) => (
                <li key={index} className="flex items-center gap-4 text-lg text-gray-300">
                  <CheckCircle className="w-6 h-6 text-cyan-400 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/397vlsv3_129A0250-D9D5-45EB-8A63-2051B1B34E52.png"
              alt="Real Estate Launch"
              className="rounded-2xl shadow-2xl"
            />
          </div>
        </div>
      </PageSection>
      
      {/* Gallery */}
      <PageSection className="bg-[#030810]">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center font-heading">
          {c.gallery} <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">{c.galleryHighlight}</span>
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {GALLERY.map((img, index) => (
            <div 
              key={index}
              className="relative aspect-square rounded-xl overflow-hidden cursor-pointer group"
              onClick={() => setLightboxImage(img.url)}
            >
              <img 
                src={img.url}
                alt={img.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                <span className="text-white font-medium">{img.title}</span>
              </div>
            </div>
          ))}
        </div>
      </PageSection>
      
      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <img src={lightboxImage} alt="Gallery" className="max-w-full max-h-[90vh] object-contain rounded-lg" />
          <button className="absolute top-6 right-6 text-white/70 hover:text-white text-4xl" onClick={() => setLightboxImage(null)}>×</button>
        </div>
      )}
      
      <PageCTA 
        title={c.ctaTitle}
        titleHighlight={c.ctaTitleHighlight}
        description={c.ctaDescription}
      />
    </PageLayout>
  );
};

export default LanzamientosInmobiliariosPage;
